import React from "react";
import useStateType from "../Stores/StateStore";
import useInsuranceType from "../Stores/InsuranceStore";
import useCohortType from "../Stores/CohortStore";
import useAgeType from "../Stores/AgeStore";
import useYearType from "../Stores/YearStore";
import useSexType from "../Stores/SexStore";

interface UnderlineProps {
  active: boolean;
  children: React.ReactNode;
}

interface ComparatorDisplayTextProps {
  graphColor: string;
  displayValue: number;
  comparator: string;
  value1: number;
  value2: number;
  graphText: string;
  decimalPoint: number;
  percentDisplay: boolean;
  costDisplay: boolean;
}

const ComparatorDisplayText: React.FC<ComparatorDisplayTextProps> = ({
  graphColor,
  displayValue,
  comparator,
  value1,
  value2,
  graphText,
  decimalPoint,
  percentDisplay,
  costDisplay,
}): JSX.Element => {
  //string lists
  const cohortStringList = [
    "HCM",
    "oHCM",
    "nHCM",
    "symptomatic HCM",
    "asymptomatic HCM",
  ];
  const ageStringList = [
    "all ages",
    "ages <18",
    "ages 18-34",
    "ages 35-44",
    "ages 45-54",
    "ages 55-64",
    "ages ≥65",
  ];
  const ageCompareLabelList = [
    "all ages",
    "ages <18",
    "ages 18-34",
    "ages 35-44",
    "ages 45-54",
    "ages 55-64",
    "ages ≥65",
  ];
  const insuranceStringList = [
    " all insurance types",
    " commercial insurance",
    " Medicaid",
    " Medicare",
  ];
  const sexStringList = ["females and males", "female", "male"];

  const yearStringList = [
    "2016-2023",
    "2016",
    "2017",
    "2018",
    "2019",
    "2020",
    "2021",
    "2022",
    "2023",
    "2024",
    "2025",
    "2026",
  ];

  const stateCompValue = useStateType((s) => s.stateCompValue);
  const insuranceCompValue = useInsuranceType((s) => s.insuranceCompValue);
  const cohortCompValue = useCohortType((s) => s.cohortCompValue);
  const sexCompValue = useSexType((s) => s.sexCompValue);
  const ageCompValue = useAgeType((s) => s.ageCompValue);
  const yearCompValue = useYearType((s) => s.yearCompValue);

  const cohortValue = useCohortType((state) => state.cohortValue);
  const ageValue = useAgeType((state) => state.ageValue);
  const yearValue = useYearType((state) => state.yearValue);
  const sexValue = useSexType((state) => state.sexValue);
  const insuranceValue = useInsuranceType((state) => state.insuranceValue);
  const stateValue = useStateType((state) => state.stateValue);

  const isDefaultCohort = cohortValue === 0;
  const isDefaultAge = ageValue === 0;
  const isDefaultSex = sexValue === 0;
  const isDefaultInsurance = insuranceValue === 0;
  const isDefaultState = stateValue === "All";
  const isDefaultYear = yearValue === 0;

  function convertGenderString(genderString: string) {
    if (genderString === "female") {
      return "females";
    } else if (genderString === "male") {
      return "males";
    } else if (genderString === "females and males") {
      return "females and males";
    } else {
      return genderString;
    }
  }

  const U: React.FC<UnderlineProps> = ({ active, children }) => (
    <span className={active ? "italic whitespace-nowrap" : "whitespace-nowrap"}>
      {children}
    </span>
  );

  const getDisplayText = (
    displayValue: number,
    comparator: string,
  ): React.ReactNode => {
    const prefix = costDisplay ? "$" : "";
    const suffix = percentDisplay ? "%" : "";

    const isStateCompare = comparator === "State";
    const isInsuranceCompare = comparator === "Insurance";
    const isCohortCompare = comparator === "Cohort";
    const isAgeCompare = comparator === "Age";
    const isSexCompare = comparator === "Sex";
    const isYearCompare = comparator === "Year";

    const isCountMetric = displayValue < 4;
    const higherLower = value1 < value2 ? "lower" : "higher";
    const formatState = (val: string) => (val === "All" ? "all states" : val);

    const v1 = Number(value1)
      .toFixed(decimalPoint)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const v2 = Number(value2)
      .toFixed(decimalPoint)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (isStateCompare) {
      if (displayValue === 0) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {" with "}
            <U active={!isDefaultInsurance}>
              {insuranceStringList[insuranceValue]}
            </U>
            {", there was a "}
            {higherLower} {"number of prevalent "}{" "}
            <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>{" "}
            {"cases per 100,000 persons in "}
            {formatState(stateCompValue[0])} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {formatState(stateCompValue[1])} ({prefix}
            {v2}
            {suffix}) {"in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {"."}
          </>
        );
      } else if (displayValue === 1) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {" with "}
            <U active={!isDefaultInsurance}>
              {insuranceStringList[insuranceValue]}
            </U>
            {", there was a "}
            {higherLower} {"number of incident "}{" "}
            <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>{" "}
            {"cases per 100,000 persons in "}
            {formatState(stateCompValue[0])} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {formatState(stateCompValue[1])} ({prefix}
            {v2}
            {suffix}) {"in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {"."}
          </>
        );
      } else if (displayValue === 2) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {" with "}
            <U active={!isDefaultInsurance}>
              {insuranceStringList[insuranceValue]}
            </U>
            {", there was a "}
            {higherLower} {"number of estimated prevalent "}{" "}
            <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>{" "}
            {"cases in "}
            {formatState(stateCompValue[0])} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {formatState(stateCompValue[1])} ({prefix}
            {v2}
            {suffix}) {"in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {"."}
          </>
        );
      } else if (displayValue === 3) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {" with "}
            <U active={!isDefaultInsurance}>
              {insuranceStringList[insuranceValue]}
            </U>
            {", there was a "}
            {higherLower} {"number of estimated incident "}{" "}
            <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>{" "}
            {"cases in "}
            {formatState(stateCompValue[0])} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {formatState(stateCompValue[1])} ({prefix}
            {v2}
            {suffix}) {"in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {"."}
          </>
        );
      } else {
        if (displayValue === 76) {
          const sexPhrase = convertGenderString(sexStringList[sexValue]);
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexPhrase}</U>
              {" of "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" there was a "}
              {higherLower}
              {" poverty in "}
              {formatState(stateCompValue[0])} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {formatState(stateCompValue[1])} ({prefix}
              {v2}
              {suffix}) {"in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {"."}
            </>
          );
        } else if (displayValue === 77) {
          const sexPhrase = convertGenderString(sexStringList[sexValue]);
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexPhrase}</U>
              {" of "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" there was a "}
              {higherLower}
              {" uninsured in "}
              {formatState(stateCompValue[0])} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {formatState(stateCompValue[1])} ({prefix}
              {v2}
              {suffix}) {"in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {"."}
            </>
          );
        } else {
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>{" "}
              {"prevalent "}
              <U active={!isDefaultCohort}>
                {cohortStringList[cohortValue]}
              </U>{" "}
              {"cases of "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {", there was a "}
              {higherLower} {graphText.toLowerCase()}
              {" in "}
              {formatState(stateCompValue[0])} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {formatState(stateCompValue[1])} ({prefix}
              {v2}
              {suffix}) {"in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {"."}
            </>
          );
        }
      }
    }

    if (isInsuranceCompare) {
      if (displayValue === 0) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {", there was a "}
            {higherLower} {"number of prevalent "}
            <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
            {" cases per 100,000 persons with "}
            {insuranceStringList[insuranceCompValue[0]]} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {insuranceStringList[insuranceCompValue[1]]} ({prefix}
            {v2}
            {suffix}) {" in "}
            <U active={!isDefaultState}>{formatState(stateValue)}</U>
            {" in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {"."}
          </>
        );
      } else if (displayValue === 1) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {", there was a "}
            {higherLower} {"number of incident "}
            <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
            {" cases per 100,000 persons with "}
            {insuranceStringList[insuranceCompValue[0]]} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {insuranceStringList[insuranceCompValue[1]]} ({prefix}
            {v2}
            {suffix}) {" in "}
            <U active={!isDefaultState}>{formatState(stateValue)}</U>
            {" in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {"."}
          </>
        );
      } else if (displayValue === 2) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {", there was a "}
            {higherLower} {"number of estimated prevalent "}
            <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
            {" cases with "}
            {insuranceStringList[insuranceCompValue[0]]} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {insuranceStringList[insuranceCompValue[1]]} ({prefix}
            {v2}
            {suffix}) {" in "}
            <U active={!isDefaultState}>{formatState(stateValue)}</U>
            {" in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {"."}
          </>
        );
      } else if (displayValue === 3) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {", there was a "}
            {higherLower} {"number of estimated incident "}
            <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
            {" cases with "}
            {insuranceStringList[insuranceCompValue[0]]} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {insuranceStringList[insuranceCompValue[1]]} ({prefix}
            {v2}
            {suffix}) {" in "}
            <U active={!isDefaultState}>{formatState(stateValue)}</U>
            {" in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {"."}
          </>
        );
      }

      if (displayValue > 3) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>{" "}
            {"prevalent "}
            <U active={!isDefaultCohort}>
              {cohortStringList[cohortValue]}
            </U>{" "}
            {"cases of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {", there was a "}
            {higherLower} {graphText.toLowerCase()}
            {" with "}
            {insuranceStringList[insuranceCompValue[0]]} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {insuranceStringList[insuranceCompValue[1]]} ({prefix}
            {v2}
            {suffix}) {" in "}
            <U active={!isDefaultState}>{formatState(stateValue)}</U>
            {" in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {"."}
          </>
        );
      }
    }

    if (isSexCompare) {
      const sexLabel1 = convertGenderString(sexStringList[sexCompValue[0]]);
      const sexLabel2 = convertGenderString(sexStringList[sexCompValue[1]]);

      if (isCountMetric) {
        if (displayValue === 0) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {" in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {", there was a "}
              {higherLower} {"number of prevalent "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases per 100,000 persons in "}
              {sexLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {sexLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        } else if (displayValue === 1) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {" in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {", there was a "}
              {higherLower} {"number of incident "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases per 100,000 persons in "}
              {sexLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {sexLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        } else if (displayValue === 2) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {" in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {", there was a "}
              {higherLower} {"number of estimated prevalent "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases in "}
              {sexLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {sexLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        } else if (displayValue === 3) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {" in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {", there was a "}
              {higherLower} {"number of estimated incident "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases in "}
              {sexLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {sexLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        }
      }

      return (
        <>
          {"Among prevalent "}
          <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>{" "}
          {"cases of "}
          <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
          {" with "}
          <U active={!isDefaultInsurance}>
            {insuranceStringList[insuranceValue]}
          </U>
          {" in "}
          <U active={!isDefaultState}>{formatState(stateValue)}</U>
          {" in "}
          <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
          {", there was a "}
          {higherLower} {graphText.toLowerCase()}
          {" in "}
          {sexLabel1} ({prefix}
          {v1}
          {suffix}) {"versus "}
          {sexLabel2} ({prefix}
          {v2}
          {suffix}){"."}
        </>
      );
    }

    if (isAgeCompare) {
      const ageLabel1 = ageCompareLabelList[ageCompValue[0]];
      const ageLabel2 = ageCompareLabelList[ageCompValue[1]];

      if (isCountMetric) {
        if (displayValue === 0) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {" in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {", there was a "}
              {higherLower} {"number of prevalent "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases per 100,000 persons in "}
              {ageLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {ageLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        } else if (displayValue === 1) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {" in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {", there was a "}
              {higherLower} {"number of incident "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases per 100,000 persons in "}
              {ageLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {ageLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        } else if (displayValue === 2) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {" in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {", there was a "}
              {higherLower} {"number of estimated prevalent "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases in "}
              {ageLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {ageLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        } else if (displayValue === 3) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {" in "}
              <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
              {", there was a "}
              {higherLower} {"number of estimated incident "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases in "}
              {ageLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {ageLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        }
      }

      return (
        <>
          {"Among "}
          <U active={!isDefaultSex}>{sexStringList[sexValue]}</U> {"prevalent "}
          <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>{" "}
          {"cases with "}
          <U active={!isDefaultInsurance}>
            {insuranceStringList[insuranceValue]}
          </U>
          {" in "}
          <U active={!isDefaultState}>{formatState(stateValue)}</U>
          {" in "}
          <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
          {", there was a "}
          {higherLower} {graphText.toLowerCase()}
          {" in "}
          {ageLabel1} ({prefix}
          {v1}
          {suffix}) {"versus "}
          {ageLabel2} ({prefix}
          {v2}
          {suffix}){"."}
        </>
      );
    }

    if (isCohortCompare) {
      const cohortLabel1 = cohortStringList[cohortCompValue[0]];
      const cohortLabel2 = cohortStringList[cohortCompValue[1]];

      if (isCountMetric) {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {" with "}
            <U active={!isDefaultInsurance}>
              {insuranceStringList[insuranceValue]}
            </U>
            {" in "}
            <U active={!isDefaultState}>{formatState(stateValue)}</U>
            {" in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {", there was a "}
            {higherLower}
            {" number of "}
            {graphText.toLowerCase()}
            {" in "}
            {cohortLabel1} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {cohortLabel2} ({prefix}
            {v2}
            {suffix}){"."}
          </>
        );
      } else {
        return (
          <>
            {"Among "}
            <U active={!isDefaultSex}>{sexStringList[sexValue]}</U>
            {" of "}
            <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
            {" with "}
            <U active={!isDefaultInsurance}>
              {insuranceStringList[insuranceValue]}
            </U>
            {" in "}
            <U active={!isDefaultState}>{formatState(stateValue)}</U>
            {" in "}
            <U active={!isDefaultYear}>{yearStringList[yearValue]}</U>
            {", there was a "}
            {higherLower} {graphText.toLowerCase()}
            {" in "}
            {cohortLabel1} ({prefix}
            {v1}
            {suffix}) {"versus "}
            {cohortLabel2} ({prefix}
            {v2}
            {suffix}){"."}
          </>
        );
      }
    }

    if (isYearCompare) {
      const yearLabel1 = yearStringList[yearCompValue[0]];
      const yearLabel2 = yearStringList[yearCompValue[1]];

      if (isCountMetric) {
        if (displayValue === 0) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexStringList[sexValue]}</U> {" of "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {", there was a "}
              {higherLower} {"number of prevalent "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases per 100,000 persons in "}
              {yearLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {yearLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        } else if (displayValue === 1) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexStringList[sexValue]}</U> {" of "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {", there was a "}
              {higherLower} {"number of incident "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases per 100,000 persons in "}
              {yearLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {yearLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        } else if (displayValue === 2) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexStringList[sexValue]}</U> {" of "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {", there was a "}
              {higherLower} {"number of estimated prevalent "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases in "}
              {yearLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {yearLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        } else if (displayValue === 3) {
          return (
            <>
              {"Among "}
              <U active={!isDefaultSex}>{sexStringList[sexValue]}</U> {" of "}
              <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
              {" with "}
              <U active={!isDefaultInsurance}>
                {insuranceStringList[insuranceValue]}
              </U>
              {" in "}
              <U active={!isDefaultState}>{formatState(stateValue)}</U>
              {", there was a "}
              {higherLower} {"number of estimated incident "}
              <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>
              {" cases in "}
              {yearLabel1} ({prefix}
              {v1}
              {suffix}) {"versus "}
              {yearLabel2} ({prefix}
              {v2}
              {suffix}){"."}
            </>
          );
        }
      }

      return (
        <>
          {"Among "}
          <U active={!isDefaultSex}>{sexStringList[sexValue]}</U> {"prevalent "}
          <U active={!isDefaultCohort}>{cohortStringList[cohortValue]}</U>{" "}
          {"cases of "}
          <U active={!isDefaultAge}>{ageStringList[ageValue]}</U>
          {" with "}
          <U active={!isDefaultInsurance}>
            {insuranceStringList[insuranceValue]}
          </U>
          {" in "}
          <U active={!isDefaultState}>{formatState(stateValue)}</U>
          {", there was a "}
          {higherLower} {graphText.toLowerCase()}
          {" in "}
          {yearLabel1} ({prefix}
          {v1}
          {suffix}) {"versus "}
          {yearLabel2} ({prefix}
          {v2}
          {suffix}){"."}
        </>
      );
    }

    return null;
  };

  return (
    <div
      style={{ backgroundColor: graphColor }}
      className="rounded-2 text-white text-[16px] xl:text-[22px] py-2 px-4 mt-16 rounded-box min-h-min shadow-md font-medium w-full whitespace-normal"
    >
      {getDisplayText(displayValue, comparator)}
    </div>
  );
};

export default ComparatorDisplayText;
